import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Search, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATS = [
  { key: "all", label: "All" },
  { key: "fruits", label: "Fruits" },
  { key: "vegetables", label: "Vegetables" },
  { key: "grocery", label: "Grocery" },
  { key: "restaurant", label: "Restaurant" },
];

const Products = () => {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [sort, setSort] = useState(params.get("sort") || "newest");
  const category = params.get("category") || "all";

  const fetchItems = async () => {
    setLoading(true);
    try {
      const p = {};
      if (category && category !== "all") p.category = category;
      if (search) p.search = search;
      if (sort && sort !== "newest") p.sort = sort;
      const { data } = await api.get("/products", { params: p });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [category, sort]);

  const setCategory = (key) => {
    const np = new URLSearchParams(params);
    if (key === "all") np.delete("category"); else np.set("category", key);
    setParams(np);
  };

  const heading = useMemo(() => {
    const c = CATS.find((x) => x.key === category);
    return c ? (c.key === "all" ? "The Market" : c.label) : "The Market";
  }, [category]);

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-12" data-testid="shop-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="tick-label mb-2">Shop</div>
          <h1 className="font-heading text-4xl sm:text-5xl text-[#2C3E2D] tracking-tight font-medium">{heading}</h1>
          <p className="text-sm text-[#6B7B6C] mt-2 max-w-xl">Fresh from farms, curated with care. Filter by category or search to find what you need.</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7B6C]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchItems()}
              placeholder="Search for mangoes, dosa…"
              className="pl-9 h-11 w-full sm:w-72 rounded-full border-[#E5E5E0] bg-white"
              data-testid="shop-search-input"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 w-full sm:w-44 rounded-full bg-white border-[#E5E5E0]" data-testid="shop-sort">
              <div className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /><SelectValue placeholder="Sort" /></div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest" data-testid="sort-newest">Newest</SelectItem>
              <SelectItem value="price_asc" data-testid="sort-price-asc">Price: Low → High</SelectItem>
              <SelectItem value="price_desc" data-testid="sort-price-desc">Price: High → Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="category-chips">
        {CATS.map((c) => (
          <button
            key={c.key}
            className={`chip ${category === c.key ? "active" : ""}`}
            onClick={() => setCategory(c.key)}
            data-testid={`category-filter-${c.key}`}
          >{c.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-[#E5E5E0] bg-white animate-pulse overflow-hidden">
              <div className="aspect-[4/3] bg-[#F0EFEA]" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-[#F0EFEA] rounded w-2/3" />
                <div className="h-3 bg-[#F0EFEA] rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center text-[#6B7B6C]" data-testid="shop-empty">No products match your filters.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="products-grid">
          {items.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      )}
    </div>
  );
};

export default Products;
